import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle, Home, Package, Mail, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const OrderConfirmationPage = () => {
  const location = useLocation();
  
  // Initialize orderInfo from location state or sessionStorage
  const [orderInfo] = useState(() => {
    if (location.state?.orderId) {
      return {
        orderId: location.state.orderId,
        email: location.state.email
      };
    }
    // Try to get from sessionStorage
    const storedOrder = sessionStorage.getItem('lastOrder');
    if (storedOrder) {
      const parsed = JSON.parse(storedOrder);
      return {
        orderId: parsed.order_id,
        email: parsed.email,
        total: parsed.total,
        items: parsed.items
      };
    }
    return null;
  });

  return (
    <div className="min-h-screen bg-ivory-50 flex items-center justify-center py-12 px-4">
      <Card className="max-w-2xl w-full bg-ivory-100 border border-ivory-300/50 shadow-luxury">
        <CardContent className="p-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          <h1 className="text-4xl font-heading font-semibold text-ivory-800 mb-4">Order Confirmed!</h1>
          <p className="text-lg text-ivory-600 font-body mb-8">
            Thank you for your order. We&apos;ve received your order and will begin processing it shortly.
          </p>

          <div className="bg-gold-500/10 border border-gold-500/30 rounded-xl p-6 mb-8" data-testid="order-confirmation-details">
            {orderInfo?.orderId ? (
              <>
                <p className="text-ivory-800 font-body mb-2">
                  <strong className="text-gold-700">Order Number:</strong>{' '}
                  <span className="font-mono text-lg">{orderInfo.orderId}</span>
                </p>
                {orderInfo.email && (
                  <p className="text-ivory-600 font-body flex items-center justify-center gap-2">
                    <Mail className="w-4 h-4 text-gold-600" />
                    Confirmation sent to <strong>{orderInfo.email}</strong>
                  </p>
                )}
                {orderInfo.total && (
                  <p className="text-ivory-700 font-body mt-2">
                    <strong>Total:</strong> ${orderInfo.total.toFixed(2)}
                  </p>
                )}
              </>
            ) : (
              <p className="text-ivory-700 font-body">
                A confirmation email has been sent to your email address with order details and tracking information.
              </p>
            )}
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="font-heading font-semibold text-lg text-ivory-800 mb-4">What happens next?</h3>
              <div className="space-y-4 text-left max-w-md mx-auto">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gold-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-gold-700 font-semibold text-sm">1</span>
                  </div>
                  <div>
                    <p className="text-ivory-800 font-medium font-body">Artwork Review</p>
                    <p className="text-ivory-500 text-sm font-body">We&apos;ll review your artwork and request approval</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gold-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-gold-700 font-semibold text-sm">2</span>
                  </div>
                  <div>
                    <p className="text-ivory-800 font-medium font-body">Production (5 business days)</p>
                    <p className="text-ivory-500 text-sm font-body">Production begins after artwork approval</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gold-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-gold-700 font-semibold text-sm">3</span>
                  </div>
                  <div>
                    <p className="text-ivory-800 font-medium font-body">Shipping</p>
                    <p className="text-ivory-500 text-sm font-body">Your order ships based on selected method</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Production Time Notice */}
            <div className="bg-ivory-100 border border-ivory-200 rounded-lg p-4 text-left max-w-md mx-auto">
              <p className="text-ivory-600 text-sm font-body">
                <strong className="text-ivory-800">Estimated delivery:</strong> 5 business days production after artwork approval + shipping transit time
              </p>
            </div>

            {/* Rush Production Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left max-w-md mx-auto" data-testid="rush-production-notice">
              <p className="text-amber-700 text-sm font-body">
                <strong>Need it sooner?</strong> Rush production may be available. Please contact{' '}
                <a 
                  href="mailto:sales@engraveca.com" 
                  className="font-semibold text-gold-700 hover:underline"
                >
                  sales@engraveca.com
                </a>
                {' '}before placing your order.
              </p>
            </div>

            {/* Track Order Link */}
            {orderInfo?.orderId && (
              <div className="bg-ivory-200/50 rounded-lg p-4 border border-ivory-300">
                <p className="text-ivory-600 font-body text-sm mb-2">
                  Want to track your order?
                </p>
                <Link 
                  to="/track-order" 
                  className="text-gold-600 hover:text-gold-700 font-medium font-body flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Track Order #{orderInfo.orderId}
                </Link>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button 
                size="lg" 
                className="btn-luxury-primary rounded-lg font-body" 
                asChild
                data-testid="back-to-home-btn"
              >
                <Link to="/">
                  <Home className="w-5 h-5 mr-2" />
                  Back to Home
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="btn-luxury-outline rounded-lg font-body" 
                asChild
              >
                <Link to="/products">
                  <Package className="w-5 h-5 mr-2" />
                  Continue Shopping
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderConfirmationPage;
